import {
  Box,
  Flex,
  Text,
  Button,
  Skeleton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@chakra-ui/react';
import { useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { NotificationContext } from 'providers/Notification';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_SM } from 'utils/constants';
import NotificationItem from '../NotificationItem';
import { Notification } from '../../types/notification';

interface NotificationPanelProps {
  trackRef: (id: string) => (el: HTMLDivElement | null) => void;
}

const NotificationPanel = ({ trackRef }: NotificationPanelProps) => {
  const {
    notifications,
    isLoading,
    isPanelOpen,
    dismissNotification,
    dismissAll,
    navigateToNotification,
    togglePanel,
  } = useContext(NotificationContext);
  const { openModal } = useContext(GlobalContext);
  const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });

  if (!isPanelOpen) return null;

  const handleViewAll = () => {
    togglePanel();
    openModal(ModalType.NotificationDrawer);
  };

  const panelHeader = (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      px={4}
      py={3}
      borderBottom="1px solid"
      borderColor="gray.100"
    >
      <Text fontWeight="bold" fontSize="md">
        Notifications
      </Text>
      {notifications.length >= 2 && (
        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => dismissAll()}>
          Dismiss All
        </Button>
      )}
    </Flex>
  );

  const panelContent = (
    <>
      {isLoading && (
        <Box p={4} display="flex" flexDirection="column" gap={3}>
          <Skeleton h="60px" borderRadius={8} />
          <Skeleton h="60px" borderRadius={8} />
          <Skeleton h="60px" borderRadius={8} />
        </Box>
      )}
      {!isLoading && notifications.length === 0 && (
        <Flex py={10} justifyContent="center" alignItems="center">
          <Text color="blackAlpha.500" fontSize="sm">
            No notifications
          </Text>
        </Flex>
      )}
      {!isLoading && notifications.length > 0 && (
        <>
          <Box maxH="300px" overflowY="auto">
            {notifications.map((notification: Notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onDismiss={dismissNotification}
                onNavigate={navigateToNotification}
                itemRef={trackRef(notification._id)}
              />
            ))}
          </Box>
          <Flex
            justifyContent="center"
            py={2}
            borderTop="1px solid"
            borderColor="gray.100"
          >
            <Button
              variant="link"
              size="sm"
              color="blackAlpha.500"
              fontWeight="normal"
              _hover={{ color: 'blackAlpha.800', textDecoration: 'underline' }}
              onClick={handleViewAll}
            >
              View all
            </Button>
          </Flex>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer placement="bottom" isOpen={isPanelOpen} onClose={togglePanel}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius={12} maxH="60vh">
          <DrawerHeader p={0}>{panelHeader}</DrawerHeader>
          <DrawerBody p={0}>{panelContent}</DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Box
      position="absolute"
      top="100%"
      right={0}
      mt={2}
      w="360px"
      bg="white"
      borderRadius={12}
      boxShadow="lg"
      zIndex={10}
      overflow="hidden"
      onClick={e => e.stopPropagation()}
    >
      {panelHeader}
      {panelContent}
    </Box>
  );
};

export default NotificationPanel;
